/* Predictive search.

   Shopify's /search/suggest.json does the matching, so there is no index of
   our own to build or let go stale. One request returns products,
   collections and pages together.

   Three things this has to get right, because they are what makes type-ahead
   feel broken when they are missed:

   - Requests can come back out of order. A slow "co" landing after a fast
     "copepod" would replace good results with stale ones, so every response
     is checked against the query that is in the box now.
   - Typing should not fire a request per keystroke. Debounced, and the
     in-flight request is aborted when a newer one starts.
   - The keyboard has to work. Arrow keys move through results, Enter opens
     the highlighted one or falls through to the full search page, Escape
     closes and returns focus to the button that opened it. */
(function () {
  'use strict';

  var LIMIT = 5;
  var DEBOUNCE = 180;

  function init(root) {
    var openBtn = root.querySelector('[data-ak-srch-open]');
    var pop = root.querySelector('[data-ak-srch-pop]');
    var input = root.querySelector('[data-ak-srch-input]');
    var list = root.querySelector('[data-ak-srch-results]');
    var hint = root.querySelector('[data-ak-srch-hint]');
    var closeBtn = root.querySelector('[data-ak-srch-close]');
    if (!openBtn || !pop || !input || !list) return;

    var timer = null;
    var controller = null;
    var active = -1;

    function items() {
      return [].slice.call(list.querySelectorAll('[data-ak-srch-item]'));
    }

    function open() {
      pop.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      root.classList.add('is-open');
      input.focus();
      input.select();
    }

    function close() {
      pop.hidden = true;
      openBtn.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-open');
      active = -1;
    }

    function highlight(next) {
      var all = items();
      if (!all.length) return;
      if (active >= 0 && all[active]) all[active].classList.remove('is-active');
      active = (next + all.length) % all.length;
      var el = all[active];
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', el.id);
    }

    function esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function render(data, q) {
      var r = (data && data.resources && data.resources.results) || {};
      var products = r.products || [];
      var pages = (r.pages || []).concat(r.collections || []);
      var html = '';
      var n = 0;

      if (products.length) {
        html += '<p class="ak-srch__group">Products</p>';
        products.forEach(function (p) {
          var id = 'ak-srch-o' + (n++);
          html += '<a class="ak-srch__item" data-ak-srch-item role="option" id="' + id +
            '" href="' + esc(p.url) + '">' +
            (p.featured_image && p.featured_image.url
              ? '<img src="' + esc(p.featured_image.url) + '&width=80" alt="" width="36" height="36" loading="lazy">'
              : '<span class="ak-srch__noimg"></span>') +
            '<span class="ak-srch__item-say"><span class="ak-srch__item-t">' + esc(p.title) + '</span>' +
            (p.price ? '<span class="ak-srch__item-p tnum">' + esc(p.price) + '</span>' : '') +
            '</span></a>';
        });
      }

      if (pages.length) {
        html += '<p class="ak-srch__group">Guides and collections</p>';
        pages.forEach(function (p) {
          var id = 'ak-srch-o' + (n++);
          html += '<a class="ak-srch__item ak-srch__item--plain" data-ak-srch-item role="option" id="' +
            id + '" href="' + esc(p.url) + '"><span class="ak-srch__item-t">' + esc(p.title) +
            '</span></a>';
        });
      }

      if (!n) {
        html = '<p class="ak-srch__none">Nothing for &ldquo;' + esc(q) + '&rdquo;. Try a shorter word.</p>';
      } else {
        html += '<a class="ak-srch__all" href="/search?q=' + encodeURIComponent(q) + '">' +
          'See everything for &ldquo;' + esc(q) + '&rdquo;</a>';
      }

      list.innerHTML = html;
      list.hidden = false;
      if (hint) hint.hidden = true;
      input.setAttribute('aria-expanded', n ? 'true' : 'false');
      active = -1;
    }

    function search(q) {
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,page,collection&resources[limit]=' + LIMIT;

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          // a slow earlier request must never overwrite a newer query
          if (!data || input.value.trim() !== q) return;
          render(data, q);
        })
        .catch(function () { /* aborted, or offline: leave what is on screen */ });
    }

    openBtn.addEventListener('click', function () {
      if (pop.hidden) open(); else close();
    });

    if (closeBtn) closeBtn.addEventListener('click', function () { close(); openBtn.focus(); });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) {
        list.hidden = true;
        list.innerHTML = '';
        if (hint) hint.hidden = false;
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      timer = setTimeout(function () { search(q); }, DEBOUNCE);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
      else if (e.key === 'Enter') {
        var all = items();
        if (active >= 0 && all[active]) {
          e.preventDefault();
          window.location.href = all[active].href;
        }
        // otherwise the form submits to the real search page, which is right
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) { close(); openBtn.focus(); }
    });

    document.addEventListener('click', function (e) {
      if (!pop.hidden && !root.contains(e.target)) close();
    });
  }

  function boot() {
    document.querySelectorAll('[data-ak-srch]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
